import { useEffect } from "react";
import { useLocation } from "react-router";

import {
  HUB_ROUTE,
  isRotaCompartilhada,
  resolveProductModule,
  type ProductModuleCode,
} from "./product-module";

/**
 * O produto ativo, incluindo o caso em que a rota sozinha não diz.
 *
 * A rota resolve quase tudo: `/seguros/*` é Seguros, `/` é o hub, o resto é
 * Consórcio. O que ela NÃO resolve é contato e cliente, que são dos dois
 * produtos — a mesma pessoa tem cota e apólice. Antes essas rotas caíam no
 * "qualquer outra é consórcio", e o efeito era ruim: quem estava em Seguros e
 * abria um contato via o menu inteiro virar Consórcio, perdendo Apólices,
 * Cobrança e Persistência da barra lateral sem ter pedido nada.
 *
 * Então guardamos a última escolha EXPLÍCITA — a última rota que decidiu
 * produto por si — e as compartilhadas herdam dela.
 *
 * sessionStorage e não localStorage: o produto é contexto de navegação, não
 * preferência. Aba nova começa limpa, e voltar amanhã não deveria abrir um
 * contato no produto de ontem.
 */
const CHAVE = "north.produtoAtivo";

const lembrado = (): ProductModuleCode | null => {
  try {
    const v = window.sessionStorage.getItem(CHAVE);
    return v === "insurance" || v === "consortium" || v === "canopus" ? v : null;
  } catch {
    return null;
  }
};

export const useProductModule = (): ProductModuleCode | null => {
  const { pathname } = useLocation();
  const daRota = resolveProductModule(pathname);

  useEffect(() => {
    if (!daRota) return;
    try {
      window.sessionStorage.setItem(CHAVE, daRota);
    } catch {
      // Armazenamento bloqueado: sem memória, a rota compartilhada cai no
      // consórcio. Degrada pro comportamento antigo em vez de quebrar.
    }
  }, [daRota]);

  if (daRota) return daRota;
  // Hub: nenhum produto ativo, de propósito — é a tela de escolher.
  if (pathname === HUB_ROUTE) return null;
  // Rota compartilhada: fica onde a pessoa estava.
  if (isRotaCompartilhada(pathname)) return lembrado() ?? "consortium";
  return "consortium";
};
